import { themeAtom } from "../../../shared/atoms";
import { log, logE, logV } from "../../../shared/services/debug-logger";
import {
  AdminFeatureMonitoringResponse,
  AdminFeatureMonitoringSectionId,
  AdminMonitoringBreakdown,
  AdminMonitoringKpi,
  AdminMonitoringTable,
  CronJobLogEnvironment,
  CRON_JOB_LOG_ENVIRONMENTS,
} from "../../models/admin-monitoring.models";
import { ADMIN_MONITORING_SECTIONS } from "../../models/admin.models";
import { getAdminFeatureMonitoring, getAdminMonitoringErrorMessage } from "../../services/admin-monitoring.service";
import { adminStyles } from "../../styles/admin.styles";
import { getDefaultAdminMonitoringEnvironment } from "../../utils/admin-monitoring-environment.utils";
import { ArrowPathIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const DEFAULT_ENVIRONMENT = getDefaultAdminMonitoringEnvironment();

const toneClasses = {
  success: "bg-[#CCE4E6] text-[#007681]",
  warning: "bg-[#FFF8E1] text-[#946200]",
  danger: "bg-[#FFEAED] text-[#BF0002]",
  neutral: "bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const getEnvironmentFromSearchParam = (value: string | null): CronJobLogEnvironment => {
  const normalized = value?.trim().toUpperCase();

  if (normalized === "QS") {
    return "QAS";
  }

  const match = CRON_JOB_LOG_ENVIRONMENTS.find((environment) => environment.value === normalized);
  return match?.value ?? DEFAULT_ENVIRONMENT;
};

const EnvironmentSelector = ({
  value,
  loading,
  onChange,
}: {
  value: CronJobLogEnvironment;
  loading: boolean;
  onChange: (value: CronJobLogEnvironment) => void;
}) => (
  <label className="text-xs font-semibold uppercase" data-test="admin-monitoring-environment-control">
    <span className="mb-1 block opacity-70">Environment</span>
    <select
      value={value}
      disabled={loading}
      data-test="admin-monitoring-environment-select"
      className="h-10 border border-gray-300 bg-transparent px-3 text-sm normal-case outline-none focus:border-green disabled:opacity-60 dark:border-gray-700"
      onChange={(event) => onChange(event.target.value as CronJobLogEnvironment)}
    >
      {CRON_JOB_LOG_ENVIRONMENTS.map((environment) => (
        <option key={environment.value} value={environment.value}>
          {environment.label}
        </option>
      ))}
    </select>
  </label>
);

const MetricTile = ({ metric }: { metric: AdminMonitoringKpi }) => (
  <div
    className={twMerge(adminStyles.card.indicator, toneClasses[metric.tone ?? "neutral"])}
    data-test={`admin-monitoring-kpi-${metric.key}`}
  >
    <p className="truncate text-[10px] font-semibold uppercase opacity-75">{metric.label}</p>
    <p className="mt-3 truncate text-5xl font-bold leading-none">{metric.value}</p>
    {metric.caption && <p className="mt-1 truncate text-xs opacity-75">{metric.caption}</p>}
  </div>
);

const KpiGrid = ({ kpis }: { kpis: AdminMonitoringKpi[] }) => (
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-test="admin-monitoring-feature-kpis">
    {kpis.map((metric) => (
      <MetricTile key={metric.key} metric={metric} />
    ))}
  </div>
);

const BreakdownPanel = ({ breakdown }: { breakdown: AdminMonitoringBreakdown }) => {
  const maxValue = Math.max(...breakdown.items.map((item) => item.value), 1);

  return (
    <section
      className={twMerge(adminStyles.card.surface, "p-4")}
      data-test={`admin-monitoring-breakdown-${breakdown.key}`}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{breakdown.title}</h2>
        <span className="text-[10px] font-semibold uppercase opacity-60">{breakdown.kind}</span>
      </div>

      {breakdown.items.length > 0 ? (
        <div className="space-y-3">
          {breakdown.items.map((item) => {
            const width = Math.max(3, Math.round((item.value / maxValue) * 100));
            const lowerLabel = item.label.toLowerCase();
            const isProblemStack =
              breakdown.kind === "stack" && /(unread|not activated|open|overdue|missing|submitted)/.test(lowerLabel);

            return (
              <div key={item.label} className="grid grid-cols-[minmax(96px,150px)_1fr_auto] items-center gap-3">
                <span className="truncate text-xs font-medium" title={item.label}>
                  {item.label}
                </span>
                <div className="h-3 min-w-0 bg-[#E9F4F3] dark:bg-gray-700">
                  <div
                    className={twMerge("h-full bg-green", isProblemStack && "bg-[#BF0002]")}
                    style={{ width: `${width}%` }}
                  />
                </div>
                <span className="w-16 text-right text-xs font-semibold">{item.value.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="border-greyscale-20 border border-dashed px-4 py-5 text-sm opacity-70 dark:border-gray-700">
          No data for this signal.
        </p>
      )}
    </section>
  );
};

const BreakdownsGrid = ({ breakdowns }: { breakdowns: AdminMonitoringBreakdown[] }) => (
  <div className="grid gap-4 2xl:grid-cols-3" data-test="admin-monitoring-feature-breakdowns">
    {breakdowns.map((breakdown) => (
      <BreakdownPanel key={breakdown.key} breakdown={breakdown} />
    ))}
  </div>
);

// Keeps row filters local to the Tech Form attention table so other monitoring pages stay unchanged.
type TechFormMonitoringFilter = "all" | "offline" | "rejoined";

const MonitoringTablePanel = ({ table }: { table: AdminMonitoringTable }) => {
  const [techFormFilter, setTechFormFilter] = useState<TechFormMonitoringFilter>("all");
  const isTechFormTable = table.key === "stalePendingTechForms";
  const filteredRows = useMemo(() => {
    if (!isTechFormTable || techFormFilter === "all") {
      return table.rows;
    }

    return table.rows.filter((row) =>
      techFormFilter === "offline" ? row.offline === "Offline" : row.rejoined === "Rejoined Fleet",
    );
  }, [isTechFormTable, table.rows, techFormFilter]);
  const filterOptions: Array<{ key: TechFormMonitoringFilter; label: string; count: number }> = [
    { key: "all", label: "All", count: table.rows.length },
    { key: "offline", label: "Offline", count: table.rows.filter((row) => row.offline === "Offline").length },
    {
      key: "rejoined",
      label: "Rejoined Fleet",
      count: table.rows.filter((row) => row.rejoined === "Rejoined Fleet").length,
    },
  ];

  return (
    <section className={twMerge(adminStyles.card.surface, "p-4")} data-test={`admin-monitoring-table-${table.key}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{table.title}</h2>
        {isTechFormTable && (
          <div className="flex flex-wrap gap-2" data-test="tech-form-monitoring-filters">
            {filterOptions.map((filter) => (
              <button
                key={filter.key}
                type="button"
                aria-pressed={techFormFilter === filter.key}
                data-test={`tech-form-monitoring-filter-${filter.key}`}
                className={twMerge(
                  "rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors",
                  techFormFilter === filter.key
                    ? "border-green bg-green text-white"
                    : "border-gray-300 bg-transparent hover:border-green dark:border-gray-700",
                )}
                onClick={() => setTechFormFilter(filter.key)}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>
        )}
      </div>

      {filteredRows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-greyscale-20 border-b dark:border-gray-700">
                {table.columns.map((column) => (
                  <th key={column.key} className="whitespace-nowrap px-3 py-2 font-semibold uppercase opacity-70">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, rowIndex) => (
                <tr
                  key={`${table.key}-${row.imoNumber ?? rowIndex}-${row.tcCode ?? "missing"}-${row.requestedOn ?? rowIndex}`}
                  className="border-greyscale-20 border-b last:border-0 dark:border-gray-700"
                >
                  {table.columns.map((column) => (
                    <td key={column.key} className="max-w-[280px] px-3 py-2 align-top">
                      {renderMonitoringTableCell(column.key, row[column.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="border-greyscale-20 border border-dashed px-4 py-5 text-sm opacity-70 dark:border-gray-700">
          {isTechFormTable && techFormFilter !== "all"
            ? "No pending Tech Forms match this filter."
            : "No rows require attention."}
        </p>
      )}
    </section>
  );
};

const TablesStack = ({ tables }: { tables: AdminMonitoringTable[] }) => (
  <div className="space-y-4" data-test="admin-monitoring-feature-tables">
    {tables.map((table) => (
      <MonitoringTablePanel key={table.key} table={table} />
    ))}
  </div>
);

const SignalPanel = ({ data }: { data: AdminFeatureMonitoringResponse }) => (
  <aside className={twMerge(adminStyles.card.tintedSurface, "p-5")} data-test="admin-monitoring-feature-signals">
    <h2 className="text-sm font-semibold">Debug signals</h2>
    <p className="mt-2 text-xs leading-5 opacity-75">Updated {formatUpdatedAt(data.updatedAt)}</p>

    <div className="mt-5 space-y-3">
      {data.signals.map((signal) => (
        <div
          key={signal}
          className="flex gap-2 border-l-2 border-green bg-white/70 p-3 text-sm leading-5 dark:bg-gray-900/40"
        >
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green" />
          <span>{signal}</span>
        </div>
      ))}
    </div>
  </aside>
);

const LoadingPanel = () => (
  <div className={adminStyles.card.loading}>
    <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin text-green" />
    Loading monitoring dashboard
  </div>
);

const FeatureMonitoringPanel = ({ section }: { section: AdminFeatureMonitoringSectionId }) => {
  const theme = useAtomValue(themeAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const environment = useMemo(() => getEnvironmentFromSearchParam(searchParams.get("environment")), [searchParams]);
  const [data, setData] = useState<AdminFeatureMonitoringResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navSection = ADMIN_MONITORING_SECTIONS.find((item) => item.id === section);

  useEffect(() => {
    if (searchParams.get("environment") === environment) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("environment", environment);
    setSearchParams(nextSearchParams, { replace: true });
  }, [environment, searchParams, setSearchParams]);

  const loadDashboard = useCallback(async () => {
    log("FeatureMonitoringPanel - loadDashboard", { section, environment });
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await getAdminFeatureMonitoring(section, environment);
      log("STATE - applyFeatureMonitoring", { tables: response.tables.length, signals: response.signals.length });
      setData(response);
    } catch (error) {
      logE(new Error("FeatureMonitoringPanel - loadDashboard - failed"));
      setErrorMessage(getAdminMonitoringErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [environment, section]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const updateEnvironment = (value: CronJobLogEnvironment) => {
    log("FeatureMonitoringPanel - updateEnvironment", { environment: value });
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("environment", value);
    setSearchParams(nextSearchParams);
  };

  logV("FeatureMonitoringPanel - BUILD", { section, loading });

  return (
    <main
      data-test="admin-monitoring-feature-panel"
      data-section={section}
      className={twMerge("min-h-0 flex-1 overflow-auto", adminStyles.page.pagePadding, theme.home.tailwind.textDefault)}
    >
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className={adminStyles.heading.eyebrow}>Monitoring</p>
          <h1 className={twMerge("mt-1", adminStyles.heading.pageTitle, theme.home.tailwind.textDefault)}>
            {navSection?.label ?? "Monitoring"}
          </h1>
          <p className={twMerge("mt-2", adminStyles.heading.body, theme.uwhcAssistant.textOutput)}>
            {data?.summary ?? "Operational signals from Snowflake for this OpsLink feature."}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <EnvironmentSelector value={environment} loading={loading} onChange={updateEnvironment} />
          <button
            type="button"
            data-test="admin-monitoring-feature-refresh"
            className="inline-flex h-10 items-center gap-2 bg-green px-4 text-sm font-semibold text-white transition-colors hover:bg-[#166b60] disabled:opacity-50"
            disabled={loading}
            onClick={() => void loadDashboard()}
          >
            <ArrowPathIcon className={twMerge("h-5 w-5", loading && "animate-spin")} />
            Refresh
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="mb-4 border border-[#F59692] bg-[#FFEAED] px-4 py-3 text-sm text-[#BF0002]">{errorMessage}</div>
      )}

      {data ? (
        <div className="grid min-h-0 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 space-y-5">
            <KpiGrid kpis={data.kpis} />
            <BreakdownsGrid breakdowns={data.breakdowns} />
            <TablesStack tables={data.tables} />
          </div>
          <SignalPanel data={data} />
        </div>
      ) : (
        <LoadingPanel />
      )}
    </main>
  );
};

function formatCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "number") {
    return value.toLocaleString();
  }

  return value;
}

function renderMonitoringTableCell(columnKey: string, value: string | number | null | undefined) {
  if (columnKey === "offline") {
    return value === "Offline" ? (
      <span
        data-test="tech-form-monitoring-offline-badge"
        className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
      >
        Offline
      </span>
    ) : (
      <span className="opacity-40">—</span>
    );
  }

  if (columnKey === "rejoined") {
    return value === "Rejoined Fleet" ? (
      <span
        data-test="tech-form-monitoring-rejoined-badge"
        className="inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800"
      >
        Rejoined Fleet
      </span>
    ) : (
      <span className="opacity-40">—</span>
    );
  }

  return <span className="line-clamp-2 break-words">{formatCell(value)}</span>;
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

export default FeatureMonitoringPanel;
